import React from 'react';
import Head from 'next/head';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Link from 'next/link';
import Image from 'next/image';
import IntegrationsSection from '../components/IntegrationsSection';
import HowItWorksSection from '../components/HowItWorksSection';
import XlimIntroSection from '../components/XlimIntroSection';

export default function ForInfrastructureEngineers() {
  return (
    <>
      <Head>
        <title>For Infrastructure Engineers | Personalia - InDesign Automation Platform</title>
        <meta name="description" content="Escape HTML-to-PDF hell. Personalia gives you a professional document API — no CSS page-break battles, no browser inconsistencies. Just production-ready output." />
      </Head>

      <Header />

      <main id="main-content">
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-16 md:py-24">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary bg-opacity-20 text-primary-light rounded-full px-4 py-2 text-sm font-medium mb-6">
                Tired of fighting CSS across page breaks?
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Turn InDesign Into a Fully Automated Document Platform.
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Integrate a proven, stable rendering engine directly into your workflows, without fighting the unpredictable output of browser‑based PDF tools.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link href="/contact" className="btn btn-primary btn-lg">
                  Get In Contact
                </Link>
                <Link href="/integrations" className="btn btn-outline-light btn-lg">
                  View Integrations
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Developer Benefits Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Build the Experience. Automate the Assets.
              </h2>
              <p className="text-xl text-gray-600">
                Let Personalia handle the content pipeline and infrastructure while you architect the experiences that matter most. No servers to manage — just scalable asset generation via API.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 flex flex-row items-start">
                <div className="mr-6 flex-shrink-0">
                  <Image src="/images/icons/Developers.png" alt="API Integration Icon" width={55} height={55} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">Simple API Integration</h3>
                  <p className="text-gray-600">
                    Connect to Personalia with our RESTful API. We handle the rendering infrastructure — you just make API calls and receive production-ready assets.
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 flex flex-row items-start">
                <div className="mr-6 flex-shrink-0">
                  <Image src="/images/icons/Easy Automation.png" alt="No-Code Connectors Icon" width={55} height={55} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">Easy Automation</h3>
                  <p className="text-gray-600">
                    Use our pre-built connectors for popular no-code automation tools to integrate Personalia without writing a single line of code.
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 flex flex-row items-start">
                <div className="mr-6 flex-shrink-0">
                  <Image src="/images/icons/Generate & Deliver.png" alt="Generate & Deliver Icon" width={55} height={55} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">Generate & Deliver</h3>
                  <p className="text-gray-600">
                    Generate content for digital and print from a single source. Scale from one request to hundreds of thousands — our infrastructure handles the load.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <HowItWorksSection userType="developer" />

        {/* Escape PDF Hell Section */}
        <section className="py-16 md:py-24 bg-white border-t border-gray-100">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Escape HTML-to-PDF Hell
              </h2>
              <p className="text-xl text-gray-600">
                You've tried Puppeteer, wkhtmltopdf, and every HTML-to-PDF library. Time for a better approach.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* The Problem */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-3">The HTML-to-PDF Problem</div>
                <h3 className="text-xl font-bold mb-4">Why CSS Wasn't Built for Documents</h3>
                <p className="text-gray-600 mb-4">
                  HTML and CSS were designed for web pages, not multi-page documents. 
                  The result? Endless workarounds and compromises.
                </p>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span><strong>Page breaks</strong> — CSS rules are inconsistent across browsers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span><strong>Headers/footers</strong> — require complex JavaScript workarounds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span><strong>Typography</strong> — limited to web fonts, no advanced controls</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span><strong>Print output</strong> — CMYK, bleeds, and spot colours are nearly impossible</span>
                  </li>
                </ul>
              </div>

              {/* The Solution */}
              <div className="bg-primary rounded-2xl p-8 text-white">
                <div className="text-sm font-semibold text-primary-light uppercase tracking-wide mb-3">The Personalia Approach</div>
                <h3 className="text-xl font-bold mb-4">Let Designers Handle Design</h3>
                <p className="opacity-90 mb-4">
                  InDesign was built for documents. Page breaks, headers, footers, and typography work as expected. 
                  Your designers create templates; you make API calls.
                </p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-primary-light mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>Clean separation</strong> — design in InDesign, data via API</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-primary-light mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>Predictable output</strong> — no browser rendering quirks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-primary-light mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>Professional quality</strong> — print-ready or screen-optimised</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-primary-light mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>Less code</strong> — no CSS hacks or browser workarounds</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center mt-10">
              <Link href="/why-personalia#html-pdf" className="text-primary hover:underline font-medium inline-flex items-center gap-2">
                See the full comparison
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Integration Section */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/2">
                <div className="relative rounded-lg overflow-hidden shadow-xl">
                  <Image
                    src="/images/zapierworkflow.png"
                    alt="Zapier Workflow"
                    width={600}
                    height={400}
                    className="w-full h-auto"
                  />
                </div>
              </div>
              <div className="md:w-1/2">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Flexible Integration Options
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Connect Personalia to your existing systems and workflows with our flexible integration options.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">RESTful API for direct integration with your applications</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">Pre-built connectors for popular no-code automation tools</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">Webhook support for event-driven workflows</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">Support for multiple output formats (PDF, JPG, PNG)</p>
                  </li>
                </ul>
                <div className="mt-8">
                  <Link href="/integrations" className="btn btn-primary">
                    View Integrations
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>


        <XlimIntroSection variant="developer" />


        {/* Use Cases Section */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Build Powerful Workflows
              </h2>
              <p className="text-xl text-gray-600">
                Integrate Personalia into your business processes to automate asset generation at any scale.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">CRM Integration</h3>
                <p className="text-gray-600 text-sm">
                  Trigger personalised collateral generation when prospects move through your pipeline. Connect to Salesforce, HubSpot, or any CRM via API or Zapier.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">E-commerce</h3>
                <p className="text-gray-600 text-sm">
                  Generate product brochures, spec sheets, and personalised offers on-demand based on customer data, cart contents, or browsing history.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Email Campaigns</h3>
                <p className="text-gray-600 text-sm">
                  Create unique, personalised attachments or embedded images for each recipient in your email campaigns at scale.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Report Generation</h3>
                <p className="text-gray-600 text-sm">
                  Automate the creation of branded reports, statements, and documents by pulling data from your databases or analytics platforms.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Real Estate & Property</h3>
                <p className="text-gray-600 text-sm">
                  Generate property brochures, flyers, and listing sheets automatically from your property management system or MLS data.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Publishing & Catalogues</h3>
                <p className="text-gray-600 text-sm">
                  Automate catalogue and publication production by connecting to your PIM or product database for always up-to-date content.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Digital and Print Section */}
        <section className="py-16 md:py-24 bg-gray-900 text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Simplify Digital & Print Workflows
              </h2>
              <p className="text-xl text-gray-300">
                One template, countless versions, multiple delivery formats – powerered by one powerful production service.
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/2">
                <h3 className="text-2xl font-semibold mb-4">Unified Asset Management</h3>
                <p className="text-gray-300 mb-6">
                Personalia unifies digital and print asset production, so you can manage all assets from one place.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-300"><span className="font-semibold">Simple API Endpoints:</span> Generate both digital and print-ready assets, or content-urls, with simple API calls</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-300"><span className="font-semibold">Output Configuration:</span> Specify output intent for digital or print across multiple file formats (PDF, JPG, PNG) with simple parameters in your API calls</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-300"><span className="font-semibold">Optimized Pipeline:</span> Easily handle desired resolution, and technical specifications for each medium</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-300"><span className="font-semibold">Reduced Development:</span> Build once, deliver everywhere — no need for separate systems for digital vs. print content</p>
                  </li>
                </ul>
                <div className="mt-8">
                  <Link href="https://developer.personalia.io/" className="btn btn-primary">
                    View API Documentation
                  </Link>
                </div>
              </div>
              <div className="md:w-1/2">
                <div className="bg-gray-800 rounded-lg p-6 font-mono text-sm text-gray-300 shadow-xl">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <div className="ml-4 text-gray-400">api-example.js</div>
                  </div>
                  <pre className="overflow-x-auto">{`// Generate digital version (low-res, RGB, for email)
const digitalResponse = await personalia.render({
  "TemplateId": "3e18cd92-39cd-400d-ab40-18284d7fd8f9",
  "Fields": {
    "Image": "Image",
    "Logo Type": "Logo Type",
    "From Name": "From Name",
    "Message": "Message",
    "To Name": "To Name"
  },
  "Output": {
    "Format": "PNG",
    "Resolution": 72
  }
});

// Generate print version (high-res, CMYK+spot, with bleeds)
const printResponse = await personalia.render({
  "TemplateId": "3e18cd92-39cd-400d-ab40-18284d7fd8f9",
  "Fields": {
     "Image": "Image",
    "Logo Type": "Logo Type",
    "From Name": "From Name",
    "Message": "Message",
    "To Name": "To Name"
  },
  "Output": {
    "Format": "PDF",
    "Quality": "Print"
  }
});`}</pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        <IntegrationsSection />

        {/* Testimonial Section */}
        <section className="py-16 md:py-24 bg-primary text-white">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <svg className="h-12 w-12 mx-auto mb-6 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              <p className="text-2xl mb-8 leading-relaxed">
                "Integrating Personalia into our workflow has been a game-changer for our development team. We can now generate beautiful, on-brand materials at scale through a simple API. Our production time has decreased by 70% while maintaining high design standards."
              </p>
              <div>
                <p className="font-semibold text-xl">Sarah Johnson</p>
                <p className="text-white text-opacity-80">Creative Director, ABC Agency</p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Build Something Creative and Amazing?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Let Personalia help you to create something that will make your clients truly happy and your team more productive. Get in contact with us and let our dedicated team can help you with your next project.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link href="/contact" className="btn btn-primary btn-lg">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
